import { useEffect, useState } from "react"
import { Modal, Form, Input, Tag, Space, Button, Select } from "antd"
import { DomainLv1FormData, DomainLv1ModalProps } from "../types"

const { TextArea } = Input
const { Option } = Select
const NAME_SPLIT_REGEX = /[\n,，;；]+/

const DomainModal = ({
  isOpen,
  onClose,
  onSave,
  domainLv1,
  loading = false,
  assistTypeOptions = [],
}: DomainLv1ModalProps) => {
  const [form] = Form.useForm<DomainLv1FormData>()
  const [rawNames, setRawNames] = useState("")
  const [parsedNames, setParsedNames] = useState<string[]>([])
  const [nameError, setNameError] = useState<string | null>(null)
  const [nameHint, setNameHint] = useState<string | null>(null)

  const resetBatchState = () => {
    setRawNames("")
    setParsedNames([])
    setNameError(null)
    setNameHint(null)
  }

  const parseNames = (input: string) => {
    return input
      .split(NAME_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  const handleParse = () => {
    const input = rawNames.trim()
    if (!input) {
      setNameError("请输入一级领域名称")
      return
    }
    const tokens = rawNames
      .split(NAME_SPLIT_REGEX)
      .map((item) => item.trim())
    const emptyCount = tokens.filter((item) => !item).length
    const inputNames = tokens.filter(Boolean)
    const uniqueInput = Array.from(new Set(inputNames))
    const inputDuplicateCount = inputNames.length - uniqueInput.length
    const existingDuplicateCount = uniqueInput.filter((name) =>
      parsedNames.includes(name)
    ).length
    const nextNames = uniqueInput
    if (nextNames.length === 0) {
      setNameError("请输入有效的一级领域名称")
      return
    }
    const merged = Array.from(new Set([...parsedNames, ...nextNames]))
    setParsedNames(merged)
    setRawNames("")
    setNameError(null)
    const hints: string[] = []
    if (emptyCount > 0) {
      hints.push(`已忽略 ${emptyCount} 个空行`)
    }
    if (inputDuplicateCount > 0) {
      hints.push(`输入内重复 ${inputDuplicateCount} 个已去重`)
    }
    if (existingDuplicateCount > 0) {
      hints.push(`与已解析重复 ${existingDuplicateCount} 个已去重`)
    }
    setNameHint(hints.length > 0 ? hints.join("；") : null)
  }

  // 当编辑的领域变化时，更新表单数据
  useEffect(() => {
    if (!isOpen) return
    resetBatchState()
    if (domainLv1) {
      form.setFieldsValue({
        name: domainLv1.name,
        note: domainLv1.note,
        assistTypeItemIds: domainLv1.assistTypeItemIds || [],
      })
    } else {
      form.resetFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // 处理表单提交
  const handleSubmit = async () => {
    const values = await form.validateFields()
    if (domainLv1) {
      await onSave({
        name: values.name?.trim?.() ?? values.name,
        note: values.note?.trim?.(),
        assistTypeItemIds: values.assistTypeItemIds || [],
      })
      return
    }

    let names = parsedNames
    if (names.length === 0 && rawNames.trim()) {
      names = parseNames(rawNames)
    }
    if (names.length === 0) {
      setNameError("请至少添加一个一级领域名称")
      return
    }
    const uniqueNames = Array.from(new Set(names))
    if (uniqueNames.length !== names.length) {
      setNameHint(`已自动去重 ${names.length - uniqueNames.length} 个重复名称`)
    }
    await onSave({
      names: uniqueNames,
      note: values.note?.trim?.(),
    })
  }

  // 处理取消
  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const modalTitle = domainLv1 ? "编辑一级领域" : "新增一级领域"

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        {domainLv1 ? (
          <Form.Item
            label="一级领域名称"
            name="name"
            rules={[{ required: true, message: "请输入一级领域名称" }]}
          >
            <Input placeholder="请输入一级领域名称" maxLength={50} showCount />
          </Form.Item>
        ) : (
          <Form.Item
            label="一级领域名称（批量粘贴）"
            required
            validateStatus={nameError ? "error" : undefined}
            help={
              nameError ||
              nameHint ||
              "支持换行、逗号分隔；解析后可删除单条名称"
            }
          >
            <div className="flex gap-2">
              <TextArea
                placeholder="例如：感知训练，认知训练（支持换行/逗号）"
                rows={3}
                value={rawNames}
                onChange={(event) => {
                  setRawNames(event.target.value)
                  if (nameError) setNameError(null)
                }}
                onPressEnter={(event) => {
                  if (event.ctrlKey || event.metaKey) {
                    event.preventDefault()
                    handleParse()
                  }
                }}
              />
              <div className="flex flex-col gap-2">
                <Button type="primary" onClick={handleParse}>
                  解析
                </Button>
                <Button
                  onClick={() => {
                    setRawNames("")
                    setParsedNames([])
                    setNameError(null)
                    setNameHint(null)
                  }}
                  disabled={rawNames.length === 0 && parsedNames.length === 0}
                >
                  清空
                </Button>
              </div>
            </div>
            {parsedNames.length > 0 && (
              <div className="mt-3">
                <div className="text-sm text-gray-500 mb-2">
                  已解析 {parsedNames.length} 个
                </div>
                <Space size={[8, 8]} wrap>
                  {parsedNames.map((name) => (
                    <Tag
                      key={name}
                      closable
                      onClose={(event) => {
                        event.preventDefault()
                        setParsedNames((prev) =>
                          prev.filter((item) => item !== name)
                        )
                      }}
                    >
                      {name}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
          </Form.Item>
        )}
        <Form.Item label="备注" name="note">
          <TextArea
            placeholder="请输入一级领域备注（可选）"
            rows={4}
            maxLength={200}
            showCount
          />
        </Form.Item>
        {domainLv1 && (
          <Form.Item label="绑定辅助类型" name="assistTypeItemIds">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="children"
              placeholder="请选择辅助类型（可多选）"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {assistTypeOptions.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.label} ({item.code}) {!item.enabled ? "【已停用】" : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

export default DomainModal
